import type { TFunction } from 'i18next';

/**
 * WHAT A CANVAS CARD SAYS ABOUT ITS SCENARIO GROUP — `docs/linked-scenarios.md` §8.3.
 *
 * "With no dedicated block, a group is invisible on the canvas unless each end says so: the
 * leader shows that blocks follow it, the follower shows which block it follows. That
 * visibility was the original reason this lives in blocks rather than a panel, so it cannot be
 * left implicit."
 *
 * ## Why a marker and not a brightness level
 *
 * The first attempt at this was an opacity exemption: everything dims to 0.4 while you edit,
 * and a leader only to 0.75. It did not work, and the reason is worth keeping — de-emphasis
 * can rank things, but it cannot say WHY one of them is ranked. A teacher saw a block that was
 * slightly less grey and had to guess what that meant, while a third brightness level crept
 * toward reading as a second selection.
 *
 * A label says it outright, cannot be mistaken for "this is editable", and keeps working when
 * nothing is selected at all — which is when §8.3's visibility actually matters, since a group
 * you have not opened is exactly the one you might not know exists.
 */
/**
 * WHO FOLLOWS THIS BLOCK — the one question both the label and the delete guard ask.
 *
 * §7.4's refusal needs it ("followed by X — unlink those first") and `scenarioLinkLabel` needs
 * it to say the same thing on the card. Two copies of a graph query would be two chances to
 * disagree about whether a block is a leader.
 */
export function scenarioFollowersOf<B extends { id: string; name?: string; data?: unknown }>(
  blockId: string,
  blocks: B[] | undefined,
): B[] {
  return (blocks ?? []).filter(b =>
    (b.data as { followsBlockId?: string } | undefined)?.followsBlockId === blockId);
}

/**
 * WHICH FOLLOWERS LOSE AN AUTHORED ANSWER IF THIS VARIANT GOES — §7.3's naming.
 *
 * Deleting a variant removes the matching row from every follower, and a row can be one of two
 * very different things. `unreviewed` means the row was COPIED when the variant appeared and
 * nobody has touched it since (§4.4), so losing it costs nothing. A row without the flag was
 * decided by a teacher, and that is work with no way back except undo — which on a phone does
 * not survive leaving the block's editor (§7.4).
 *
 * So the guard asks the narrow question rather than "does anyone follow me". While a scenario is
 * being built every row is an untouched copy and this returns nothing, which is what keeps the
 * delete silent in the case that happens most. The warning appears exactly when something is at
 * stake, and names what.
 */
export function followersLosingAnswers<B extends { id: string; name?: string; data?: unknown }>(
  leaderId: string,
  scenarioId: string,
  blocks: B[] | undefined,
): B[] {
  return scenarioFollowersOf(leaderId, blocks).filter(b => {
    const rows = (b.data as { scenarioBranches?: { scenarioId: string; unreviewed?: boolean }[] } | undefined)
      ?.scenarioBranches;
    const row = rows?.find(r => r.scenarioId === scenarioId);
    return !!row && !row.unreviewed;
  });
}

/**
 * The same block with this variant's row gone.
 *
 * Returns the data UNCHANGED when there is no such row, so a caller can map every follower
 * without first working out which ones are affected — and so an unchanged block is identical by
 * reference and never looks like an edit to anything watching.
 */
export function withScenarioRowRemoved(
  data: unknown,
  scenarioId: string,
): Record<string, unknown> {
  const d = (data ?? {}) as Record<string, unknown>;
  const rows = d.scenarioBranches as { scenarioId: string }[] | undefined;
  if (!rows?.some(r => r.scenarioId === scenarioId)) return d;
  return { ...d, scenarioBranches: rows.filter(r => r.scenarioId !== scenarioId) };
}

export function scenarioLinkLabel(
  block: { id: string; data?: unknown },
  blocks: { id: string; name?: string; data?: unknown }[] | undefined,
  t: TFunction,
): { text: string; a11y: string } | null {
  const all = blocks ?? [];
  const nameOf = (b: { id: string; name?: string }) => b.name || b.id;

  // The FOLLOWER end: which block decides my answer.
  const followsId = (block.data as { followsBlockId?: string } | undefined)?.followsBlockId;
  if (followsId) {
    const leader = all.find(b => b.id === followsId);
    // A dangling link says nothing rather than naming a block that is not there — deleting a
    // leader is handled as its own case (§7.4), not announced by a stale label.
    if (!leader) return null;
    return {
      text: nameOf(leader),
      a11y: t('blocks.scenario.followsLabel', { name: nameOf(leader) }),
    };
  }

  // The LEADER end: who follows me. Plural, because §2.5 allows several.
  const followers = scenarioFollowersOf(block.id, all);
  if (followers.length === 0) return null;
  const names = followers.map(nameOf).join(', ');
  return { text: shownNames(followers.map(nameOf), t), a11y: t('blocks.scenario.followedByLabel', { names }) };
}

/**
 * HOW MANY NAMES THE PILL SHOWS — two, then a count.
 *
 * Nothing caps how many blocks may follow a leader, and the pill is a 10px label in a 9rem box
 * on the card's top edge. Three names overflow it on a phone, and what CSS truncation leaves
 * behind is a name cut mid-word: worse than a count, because a half-name reads as a real one.
 *
 * Two plus `+N` rather than "and N more", which is the clearer phrase and does not fit — the
 * overflow marker has to be shorter than the thing it is rescuing or it has not rescued it.
 * Nothing is lost: the full list is spoken in `followedByLabel` just below, where width is not
 * a constraint, and the whole group is on screen anyway once you know to look for it.
 */
const SHOWN_FOLLOWERS = 2;

/** Shared with the DRIVEN pill, which has the same box and the same overflow problem. */
export function shownNames(names: string[], t: TFunction): string {
  const shown = names.slice(0, SHOWN_FOLLOWERS).join(', ');
  const hidden = names.length - SHOWN_FOLLOWERS;
  return hidden > 0 ? t('blocks.scenario.followedByOverflow', { names: shown, count: hidden }) : shown;
}
